import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Eye, EyeOff, Download, PackageCheck, X, Search, Filter, Pencil, Undo2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  getSuppliers, createSupplier, toggleSupplier, deleteSupplier, renameSupplier,
  getSupplierLots, addSupplierLot, updateSupplierLot, receiveSupplierLot, deleteSupplierLot,
  getSupplierReceipts, undoSupplierReceipt, updateFabric, toggleSupplierFabric, formatApiError,
} from "@/lib/api";
import { slug, formatDate, formatDateTime, evalFormula, deriveFabricCodes, itemMatchesQuery, fabricCodeLabel } from "@/lib/fabricData";
import { FormulaInput } from "@/components/FormulaInput";

const fmt = (n) => Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 });
const keyOf = (f, c) => `${f}|${c}`;
const normNoSpace = (s) => String(s || "").replace(/\s+/g, "").toLowerCase();

// Excel-style multi-select column filter dropdown for the Order History table.
function ColumnFilter({ label, align = "left", values, selected, onToggle, onClear }) {
  const [open, setOpen] = useState(false);
  const active = selected.size > 0;
  return (
    <div className="relative inline-flex items-center gap-1">
      <span>{label}</span>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid={`receipt-filter-${slug(label)}`}
        className={`p-0.5 ${active ? "text-emerald-400" : "text-white/30 hover:text-white"}`}
        title={`Filter ${label}`}
      >
        <Filter className="h-3 w-3" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className={`absolute top-5 ${align === "right" ? "right-0" : "left-0"} z-50 w-56 max-h-64 overflow-auto bg-[#141414] border border-white/15 p-2 shadow-2xl shadow-black/60 normal-case tracking-normal`}>
            <div className="flex items-center justify-between mb-1.5 pb-1.5 border-b border-white/10">
              <span className="text-[10px] text-white/40">{selected.size} selected</span>
              <button type="button" onClick={onClear} data-testid={`receipt-filter-clear-${slug(label)}`} className="text-[10px] uppercase tracking-wider text-white/60 hover:text-white">Clear</button>
            </div>
            {values.length === 0 && <div className="text-[11px] text-white/40 py-1">No values</div>}
            {values.map((v) => (
              <label key={v || "(blank)"} className="flex items-center gap-2 py-1 text-xs text-white/90 cursor-pointer hover:text-white">
                <input type="checkbox" checked={selected.has(v)} onChange={() => onToggle(v)} className="accent-white h-3.5 w-3.5 shrink-0" />
                <span className="break-words">{v === "" ? "(blank)" : v}</span>
              </label>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export function SuppliersTab({ token, fabrics, stock, colors, units, onStockRefresh }) {
  const [suppliers, setSuppliers] = useState([]);
  const [selected, setSelected] = useState(null);
  const [lots, setLots] = useState({}); // fabric|color -> [lot, ...]
  const [receipts, setReceipts] = useState([]);
  const [newName, setNewName] = useState("");
  const [pickedFabric, setPickedFabric] = useState("");
  const [search, setSearch] = useState("");
  const [showHidden, setShowHidden] = useState(false);
  const [itemLimit, setItemLimit] = useState(20);
  const [receiptLimit, setReceiptLimit] = useState(20);
  const [receiptSearch, setReceiptSearch] = useState("");
  const emptyColFilters = { date: new Set(), fabric: new Set(), color: new Set(), qty: new Set(), remark: new Set() };
  const [colFilters, setColFilters] = useState(emptyColFilters);
  useEffect(() => { setItemLimit(20); }, [search, pickedFabric]);
  useEffect(() => { setReceiptLimit(20); }, [selected?.id]);
  useEffect(() => { setReceiptSearch(""); setColFilters({ date: new Set(), fabric: new Set(), color: new Set(), qty: new Set(), remark: new Set() }); }, [selected?.id]);

  // ----- Order History (receipts) — column values, filters + search -----
  const codeMap = useMemo(() => deriveFabricCodes(stock, fabrics), [stock, fabrics]);
  const codeOf = (f) => fabricCodeLabel(f, codeMap);
  const receiptDate = (r) => (r.confirmed_at ? formatDate(r.confirmed_at) : "");
  const receiptQty = (r) => `${fmt(r.quantity)} ${r.unit || ""}`.trim();
  const toggleColFilter = (col, v) => setColFilters((p) => {
    const next = new Set(p[col]);
    next.has(v) ? next.delete(v) : next.add(v);
    return { ...p, [col]: next };
  });
  const clearColFilter = (col) => setColFilters((p) => ({ ...p, [col]: new Set() }));
  const colValues = (col, valFn) => Array.from(new Set((receipts || []).map(valFn))).sort((a, b) => String(a).localeCompare(String(b)));
  const filteredReceipts = useMemo(() => {
    const term = normNoSpace(receiptSearch);
    return (receipts || []).filter((r) => {
      if (colFilters.date.size && !colFilters.date.has(receiptDate(r))) return false;
      if (colFilters.fabric.size && !colFilters.fabric.has(r.fabric || "")) return false;
      if (colFilters.color.size && !colFilters.color.has(r.color || "")) return false;
      if (colFilters.qty.size && !colFilters.qty.has(receiptQty(r))) return false;
      if (colFilters.remark.size && !colFilters.remark.has(r.remark || "")) return false;
      if (term) {
        const hay = normNoSpace(`${receiptDate(r)} ${r.fabric} ${r.color} ${r.quantity} ${r.unit} ${r.remark}`);
        if (!hay.includes(term) && !itemMatchesQuery({ fabric: r.fabric, color: r.color }, receiptSearch, codeMap)) return false;
      }
      return true;
    });
  }, [receipts, receiptSearch, colFilters, codeMap]);
  useEffect(() => { setReceiptLimit(20); }, [receiptSearch, colFilters]);

  const hexByName = useMemo(() => {
    const m = {};
    (colors || []).forEach((c) => { if (c && c.name) m[c.name] = c.hex; });
    return m;
  }, [colors]);
  const colorHex = (s) => s.hex || hexByName[s.color] || "#888";
  const hiddenSet = useMemo(() => new Set(selected?.hidden_fabrics || []), [selected]);

  const loadSuppliers = async () => {
    try {
      const list = await getSuppliers(token);
      setSuppliers(list);
      if (list.length && !list.some((s) => s.id === selected?.id)) setSelected(list[0]);
      if (!list.length) setSelected(null);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const loadLots = async (sid) => {
    try {
      const rows = await getSupplierLots(token, sid);
      const map = {};
      rows.forEach((r) => { const k = keyOf(r.fabric, r.color); (map[k] = map[k] || []).push(r); });
      setLots(map);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    try { setReceipts(await getSupplierReceipts(token, sid)); } catch { setReceipts([]); }
  };

  useEffect(() => { loadSuppliers(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { if (selected) loadLots(selected.id); }, [selected?.id]); // eslint-disable-line

  const addSupplier = async () => {
    if (!newName.trim()) return toast.error("Enter a supplier name");
    try {
      const res = await createSupplier(token, newName.trim());
      setNewName("");
      await loadSuppliers();
      setSelected(res.supplier);
      toast.success(`Added supplier ${res.supplier.name}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const doToggle = async (s) => {
    try { await toggleSupplier(token, s.id); loadSuppliers(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const doRename = async (s) => {
    const name = window.prompt("Supplier name:", s.name);
    if (name === null) return;
    if (!name.trim()) return toast.error("Supplier name cannot be empty");
    try {
      await renameSupplier(token, s.id, name.trim());
      if (selected?.id === s.id) setSelected({ ...selected, name: name.trim() });
      loadSuppliers();
      onStockRefresh && onStockRefresh();
      toast.success("Supplier renamed — updated everywhere");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const doDelete = async (s) => {
    if (!window.confirm(`Delete supplier "${s.name}" and all its lots? This cannot be undone.`)) return;
    try {
      await deleteSupplier(token, s.id);
      if (selected?.id === s.id) setSelected(null);
      loadSuppliers();
      toast.success("Supplier deleted");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const hideFabric = async (fabric) => {
    if (!selected) return;
    try {
      const res = await toggleSupplierFabric(token, selected.id, fabric);
      setSelected((s) => ({ ...s, hidden_fabrics: res.hidden_fabrics }));
      setSuppliers((list) => list.map((s) => (s.id === selected.id ? { ...s, hidden_fabrics: res.hidden_fabrics } : s)));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  // ----- Lot handlers -----
  const addLot = async (fabric, color, unit) => {
    if (!selected) return;
    try {
      const res = await addSupplierLot(token, selected.id, { fabric, color, unit, quantity: 0 });
      const k = keyOf(fabric, color);
      setLots((p) => ({ ...p, [k]: [...(p[k] || []), res.lot] }));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const setLotField = (k, lotId, field, value) =>
    setLots((p) => ({ ...p, [k]: (p[k] || []).map((l) => (l.id === lotId ? { ...l, [field]: value } : l)) }));

  const saveLot = async (k, lotId) => {
    const lot = (lots[k] || []).find((l) => l.id === lotId);
    if (!lot) return;
    try {
      await updateSupplierLot(token, lotId, {
        fabric: lot.fabric, color: lot.color,
        dispatch_date: lot.dispatch_date || null,
        arrival_date: lot.arrival_date || null,
        quantity: evalFormula(lot.quantity) ?? 0,
        unit: lot.unit || "kg",
        remark: lot.remark || "",
      });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const receiveLot = async (k, lot) => {
    const qty = evalFormula(lot.quantity) ?? 0;
    if (qty <= 0) return toast.error("Enter a quantity greater than 0 before marking received");
    if (!window.confirm(`Mark received ${fmt(qty)} ${lot.unit} of ${lot.fabric} · ${lot.color}? This adds it to live stock.`)) return;
    try {
      const res = await receiveSupplierLot(token, lot.id);
      setLots((p) => ({ ...p, [k]: (p[k] || []).filter((l) => l.id !== lot.id) }));
      toast.success(`Received — ${lot.fabric} · ${lot.color}: ${fmt(res.prev)} → ${fmt(res.new)}`);
      if (selected) { getSupplierReceipts(token, selected.id).then(setReceipts).catch(() => {}); }
      onStockRefresh && onStockRefresh();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const removeLot = async (k, lot) => {
    try {
      await deleteSupplierLot(token, lot.id);
      setLots((p) => ({ ...p, [k]: (p[k] || []).filter((l) => l.id !== lot.id) }));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const doUndoReceipt = async (r) => {
    if (!window.confirm(`Undo this receipt?\n\n${fmt(r.quantity)} ${r.unit} of ${r.fabric} · ${r.color} will be removed from live stock and any vendor call pending it set off will be restored.`)) return;
    try {
      const res = await undoSupplierReceipt(token, r.id);
      setReceipts((p) => p.filter((x) => x.id !== r.id));
      if (res.clamped) {
        toast.warning(`Receipt undone — only ${fmt(res.removed)} ${r.unit} could be rolled back; the rest had already been sold since it was received.`);
      } else {
        toast.success(`Receipt undone — ${r.fabric} · ${r.color}: ${fmt(res.prev)} → ${fmt(res.new)}`);
      }
      onStockRefresh && onStockRefresh();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const changeUnit = async (fabricObj, unit) => {
    try {
      await updateFabric(token, fabricObj.id, fabricObj.name, fabricObj.code || "", unit);
      toast.success(`${fabricObj.name} unit set to ${unit} (applies to all its stock)`);
      onStockRefresh && onStockRefresh();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const stockByFabric = useMemo(() => {
    const m = {};
    (stock || []).forEach((s) => {
      if (s.active === false) return;
      (m[s.fabric] = m[s.fabric] || []).push(s);
    });
    return m;
  }, [stock]);

  const isLow = (s) => s.min_kg != null && s.min_kg > 0 && (s.available_kg ?? 0) <= s.min_kg;

  // Search: fabric name OR derived code OR colour OR any lot remark under the
  // item; case-insensitive, ignores spaces.
  const searching = search.trim() !== "";
  const searchMatches = useMemo(() => {
    if (!searching) return [];
    const term = normNoSpace(search);
    return (stock || [])
      .filter((s) => {
        if (s.active === false) return false;
        if (itemMatchesQuery(s, search, codeMap)) return true;
        const itemLots = lots[keyOf(s.fabric, s.color)] || [];
        return itemLots.some((l) => term && normNoSpace(l.remark).includes(term));
      })
      .sort((a, b) => (a.fabric === b.fabric ? a.color.localeCompare(b.color) : a.fabric.localeCompare(b.fabric)));
  }, [stock, search, searching, codeMap, lots]);

  const fabricUnit = (name) => ((fabrics || []).find((f) => f.name === name) || {}).unit || "kg";

  const renderItem = (s, unitFallback, showFabric) => {
    const k = keyOf(s.fabric, s.color);
    const itemLots = lots[k] || [];
    const low = isLow(s);
    return (
      <div key={k} data-testid={`supplier-row-${slug(s.fabric)}-${slug(s.color)}`} className={`px-4 py-3 ${low ? "bg-red-500/5" : ""}`}>
        <div className="flex items-center justify-between gap-2 mb-2">
          <div className="flex items-center gap-2 min-w-0">
            <span className="h-4 w-4 border border-white/20 shrink-0" style={{ backgroundColor: colorHex(s) }} />
            <span className={`text-sm truncate ${low ? "text-red-300 font-semibold" : "text-white"}`}>{showFabric ? <span className="text-white/50">{s.fabric}{codeOf(s.fabric) ? ` (${codeOf(s.fabric)})` : ""} · </span> : null}{s.color}</span>
            <span className="text-[10px] text-white/40 tabular">· in stock {fmt(s.available_kg)} {unitFallback}{low ? " · low" : ""}</span>
          </div>
          <Button onClick={() => addLot(s.fabric, s.color, unitFallback)} data-testid={`supplier-addlot-${slug(s.fabric)}-${slug(s.color)}`}
            className="rounded-none h-7 bg-white text-black hover:bg-white/90 text-[11px] uppercase tracking-wider px-2 shrink-0">
            <Plus className="h-3.5 w-3.5 mr-1" /> Add Lot
          </Button>
        </div>

        {itemLots.length === 0 ? (
          <p className="text-[11px] text-white/30 pl-6">No lots in transit. Tap "Add Lot" to track a dispatch.</p>
        ) : (
          <div className="space-y-1.5 pl-6" data-testid={`supplier-lots-${slug(s.fabric)}-${slug(s.color)}`}>
            {itemLots.map((lot, li) => (
              <div key={lot.id} data-testid={`supplier-lot-${lot.id}`} className="bg-[#0F0F0F] border border-white/10 p-2 space-y-2">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-2 items-end">
                <div className="md:col-span-1 text-[10px] uppercase tracking-wider text-white/30 hidden md:block">Lot {li + 1}</div>
                <div className="md:col-span-2">
                  <Label className="text-[9px] uppercase tracking-wider text-white/30 block mb-0.5">Qty ({lot.unit || unitFallback})</Label>
                  <FormulaInput value={lot.quantity ?? ""} onValueChange={(val) => setLotField(k, lot.id, "quantity", val)} onCommit={() => saveLot(k, lot.id)}
                    placeholder="e.g. 25 or 20+5" data-testid={`supplier-lot-qty-${lot.id}`} className="rounded-none h-8 bg-[#141414] border-white/10 text-white text-xs px-1.5 focus-visible:ring-1 focus-visible:ring-white/30" />
                </div>
                <div className="md:col-span-3">
                  <Label className="text-[9px] uppercase tracking-wider text-white/30 block mb-0.5">Dispatch date</Label>
                  <Input type="date" value={lot.dispatch_date || ""} onChange={(e) => setLotField(k, lot.id, "dispatch_date", e.target.value)} onBlur={() => saveLot(k, lot.id)}
                    data-testid={`supplier-lot-dispatch-${lot.id}`} className="rounded-none h-8 bg-[#141414] border-white/10 text-white text-xs px-1.5 focus-visible:ring-1 focus-visible:ring-white/30" />
                </div>
                <div className="md:col-span-3">
                  <Label className="text-[9px] uppercase tracking-wider text-white/30 block mb-0.5">Expected arrival</Label>
                  <Input type="date" value={lot.arrival_date || ""} onChange={(e) => setLotField(k, lot.id, "arrival_date", e.target.value)} onBlur={() => saveLot(k, lot.id)}
                    data-testid={`supplier-lot-arrival-${lot.id}`} className="rounded-none h-8 bg-[#141414] border-white/10 text-white text-xs px-1.5 focus-visible:ring-1 focus-visible:ring-white/30" />
                </div>
                <div className="md:col-span-3 flex items-center gap-1.5">
                  <Button onClick={() => receiveLot(k, lot)} data-testid={`supplier-lot-receive-${lot.id}`}
                    className="rounded-none h-8 flex-1 bg-emerald-600 text-white hover:bg-emerald-500 text-[11px] uppercase tracking-wider transition-colors">
                    <PackageCheck className="h-3.5 w-3.5 mr-1" /> Mark Received
                  </Button>
                  <Button onClick={() => removeLot(k, lot)} data-testid={`supplier-lot-delete-${lot.id}`} variant="outline" title="Delete lot (no stock added)"
                    className="rounded-none h-8 w-8 p-0 border-red-500/40 bg-transparent text-red-400 hover:bg-red-500 hover:text-white shrink-0">
                    <X className="h-4 w-4" />
                  </Button>
                </div>
                </div>
                <div>
                  <Label className="text-[9px] uppercase tracking-wider text-white/30 block mb-0.5">Remark (optional)</Label>
                  <Input value={lot.remark || ""} onChange={(e) => setLotField(k, lot.id, "remark", e.target.value)} onBlur={() => saveLot(k, lot.id)}
                    placeholder="Bill / challan no., transporter, or any note…" data-testid={`supplier-lot-remark-${lot.id}`}
                    className="rounded-none h-8 bg-[#141414] border-white/10 text-white text-xs px-1.5 focus-visible:ring-1 focus-visible:ring-white/30" />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const downloadCSV = () => {
    if (!selected) return;
    const rows = [["Fabric", "Colour", "Lot Qty", "Unit", "Dispatch Date", "Expected Arrival", "Remark", "Downloaded On"]];
    const today = formatDate(new Date());
    Object.values(lots).flat().forEach((l) => {
      rows.push([l.fabric, l.color, fmt(l.quantity), l.unit || "", formatDate(l.dispatch_date), formatDate(l.arrival_date), l.remark || "", today]);
    });
    if (rows.length === 1) return toast.error("No lots to export for this supplier");
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `supplier-${slug(selected.name)}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Exported supplier lot sheet");
  };

  const downloadReceiptsCSV = () => {
    if (!selected) return;
    if (filteredReceipts.length === 0) return toast.error("No confirmed receipts to export for this supplier");
    const rows = [["Confirmed On", "Item", "Colour", "Quantity Received", "Unit", "Remarks"]];
    filteredReceipts.forEach((r) => rows.push([formatDateTime(r.confirmed_at), r.fabric, r.color, fmt(r.quantity), r.unit || "", r.remark || ""]));
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `supplier-${slug(selected.name)}-receipts-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Exported receipt history");
  };

  const activeFabrics = (fabrics || []).filter((f) => f.active !== false);
  const visibleFabrics = showHidden ? activeFabrics : activeFabrics.filter((f) => !hiddenSet.has(f.name));
  const currentFabric = visibleFabrics.find((f) => f.name === pickedFabric) || visibleFabrics[0] || null;

  useEffect(() => {
    if (visibleFabrics.length && !visibleFabrics.some((f) => f.name === pickedFabric)) {
      setPickedFabric(visibleFabrics[0].name);
    }
  }, [fabrics, showHidden, selected?.id]); // eslint-disable-line

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6" data-testid="suppliers-panel">
      {/* Supplier list */}
      <div className="lg:col-span-3 space-y-3">
        <div className="border border-white/10 bg-[#0A0A0A] p-3">
          <Label className="text-[10px] uppercase tracking-[0.2em] text-white/40 block mb-1.5">New supplier</Label>
          <div className="flex gap-2">
            <Input value={newName} onChange={(e) => setNewName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addSupplier()}
              placeholder="Supplier name" data-testid="new-supplier-name"
              className="rounded-none h-9 bg-[#141414] border-white/10 text-white text-sm focus-visible:ring-2 focus-visible:ring-white/30" />
            <Button onClick={addSupplier} data-testid="add-supplier-btn" className="rounded-none h-9 bg-white text-black hover:bg-white/90 px-3"><Plus className="h-4 w-4" /></Button>
          </div>
        </div>
        <div className="border border-white/10 divide-y divide-white/10">
          {suppliers.length === 0 && <div className="p-4 text-xs text-white/40" data-testid="no-suppliers">No suppliers yet.</div>}
          {suppliers.map((s) => (
            <div key={s.id} data-testid={`supplier-item-${slug(s.name)}`}
              className={`flex items-center gap-2 px-3 py-2.5 cursor-pointer transition-colors ${selected?.id === s.id ? "bg-white/10" : "hover:bg-white/[0.03]"} ${s.active === false ? "opacity-50" : ""}`}
              onClick={() => setSelected(s)}>
              <span className="flex-1 min-w-0 truncate text-sm text-white">{s.name}{s.active === false && <span className="ml-1 text-[10px] text-white/40">(hidden)</span>}</span>
              <button onClick={(e) => { e.stopPropagation(); doRename(s); }} data-testid={`supplier-rename-${slug(s.name)}`} title="Rename" className="text-white/50 hover:text-white">
                <Pencil className="h-4 w-4" />
              </button>
              <button onClick={(e) => { e.stopPropagation(); doToggle(s); }} data-testid={`supplier-toggle-${slug(s.name)}`} title={s.active === false ? "Show" : "Hide"} className="text-white/50 hover:text-white">
                {s.active === false ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
              <button onClick={(e) => { e.stopPropagation(); doDelete(s); }} data-testid={`supplier-delete-${slug(s.name)}`} title="Delete" className="text-red-400/70 hover:text-red-400">
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Selected supplier — lot sheet */}
      <div className="lg:col-span-9">
        {!selected ? (
          <div className="border border-white/10 py-20 text-center text-white/40 text-sm">Select or add a supplier to record lots.</div>
        ) : (
          <div className="space-y-5">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h2 className="font-display font-black text-2xl tracking-tight text-white" data-testid="supplier-title">{selected.name}</h2>
              <Button variant="outline" onClick={downloadCSV} data-testid="supplier-download-btn" className="rounded-none border-white/20 bg-transparent text-white hover:bg-white hover:text-black text-xs uppercase tracking-wider transition-colors">
                <Download className="h-3.5 w-3.5 mr-2" /> Download (Excel)
              </Button>
            </div>

            <div className="flex flex-wrap items-center gap-2 p-3 border border-white/10 bg-[#0A0A0A]">
              <Label className="text-[10px] uppercase tracking-[0.2em] text-white/40">Fabric type</Label>
              <Select value={currentFabric?.name || ""} onValueChange={setPickedFabric} disabled={searching}>
                <SelectTrigger data-testid="supplier-fabric-picker" className="h-9 w-56 rounded-none bg-[#141414] border-white/10 text-white text-sm focus:ring-2 focus:ring-white/30 disabled:opacity-40">
                  <SelectValue placeholder="Select a fabric type" />
                </SelectTrigger>
                <SelectContent className="rounded-none bg-[#141414] border-white/10 text-white max-h-72">
                  {visibleFabrics.map((f) => <SelectItem key={f.id || f.name} value={f.name}>{f.name}{codeOf(f.name) ? ` (${codeOf(f.name)})` : ""}{hiddenSet.has(f.name) ? " · hidden" : ""}</SelectItem>)}
                </SelectContent>
              </Select>
              {currentFabric && !searching && (
                <Button onClick={() => hideFabric(currentFabric.name)} variant="outline" data-testid={`supplier-hide-fabric-${slug(currentFabric.name)}`}
                  title={hiddenSet.has(currentFabric.name) ? "Unhide this fabric for this supplier" : "Hide this fabric for this supplier"}
                  className="rounded-none h-9 border-white/20 bg-transparent text-white hover:bg-white hover:text-black text-xs uppercase tracking-wider transition-colors">
                  {hiddenSet.has(currentFabric.name) ? <><Eye className="h-3.5 w-3.5 mr-1.5" /> Unhide</> : <><EyeOff className="h-3.5 w-3.5 mr-1.5" /> Hide</>}
                </Button>
              )}
              {hiddenSet.size > 0 && (
                <Button onClick={() => setShowHidden((v) => !v)} variant="outline" data-testid="supplier-show-hidden-toggle"
                  className={`rounded-none h-9 border-white/20 bg-transparent hover:bg-white hover:text-black text-xs uppercase tracking-wider transition-colors ${showHidden ? "bg-white text-black" : "text-white"}`}>
                  {showHidden ? "Hide hidden" : `Show hidden (${hiddenSet.size})`}
                </Button>
              )}
              <div className="relative flex-1 min-w-[180px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/30" />
                <Input value={search} onChange={(e) => setSearch(e.target.value)} data-testid="supplier-search"
                  placeholder="Search any item across all fabrics & suppliers…"
                  className="rounded-none h-9 pl-9 bg-[#141414] border-white/10 text-white text-sm focus-visible:ring-2 focus-visible:ring-white/30" />
                {searching && (
                  <button onClick={() => setSearch("")} data-testid="supplier-search-clear" className="absolute right-2 top-1/2 -translate-y-1/2 h-6 w-6 flex items-center justify-center text-white/40 hover:text-white"><X className="h-4 w-4" /></button>
                )}
              </div>
            </div>

            {searching ? (
              <div className="border border-white/10" data-testid="supplier-search-results">
                <div className="flex items-center gap-2 px-4 py-2.5 border-b border-white/10 bg-[#141414]">
                  <span className="font-display font-bold text-white">Search results</span>
                  <span className="text-white/40 text-sm">({searchMatches.length})</span>
                </div>
                {searchMatches.length === 0 ? (
                  <div className="px-4 py-6 text-xs text-white/40" data-testid="supplier-search-empty">No items match "{search}".</div>
                ) : (
                  <div className="divide-y divide-white/10">
                    {searchMatches.slice(0, itemLimit).map((s) => renderItem(s, fabricUnit(s.fabric), true))}
                    {searchMatches.length > itemLimit && (
                      <div className="flex justify-center py-4">
                        <Button onClick={() => setItemLimit((l) => l + 20)} variant="outline" data-testid="supplier-search-load-more" className="rounded-none h-9 px-6 border-white/20 bg-transparent text-white hover:bg-white hover:text-black text-xs uppercase tracking-wider transition-colors">
                          Load More ({searchMatches.length - itemLimit} more)
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : !currentFabric ? (
              <div className="border border-white/10 py-16 text-center text-white/40 text-sm">No fabric types exist yet. Add them from Manage Stock first.</div>
            ) : (() => {
              const f = currentFabric;
              const rows = stockByFabric[f.name] || [];
              const fabricLow = rows.some(isLow);
              const unitFallback = f.unit || "kg";
              return (
                <div className={`border ${fabricLow ? "border-red-500/50" : "border-white/10"}`} data-testid={`supplier-fabric-${slug(f.name)}`}>
                  <div className={`flex flex-wrap items-center justify-between gap-3 px-4 py-2.5 border-b border-white/10 ${fabricLow ? "bg-red-500/10" : "bg-[#141414]"}`}>
                    <div className="flex items-center gap-2">
                      <span className="font-display font-bold text-white">{f.name}</span>
                      {codeOf(f.name) && <span className="text-[10px] uppercase tracking-[0.15em] text-black bg-white px-1.5 py-0.5" data-testid={`supplier-fabric-code-${slug(f.name)}`}>{codeOf(f.name)}</span>}
                      {fabricLow && <span data-testid={`supplier-low-flag-${slug(f.name)}`} className="text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 border border-red-500 bg-red-500/15 text-red-300 font-bold">Low stock</span>}
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span className="text-[10px] uppercase tracking-[0.2em] text-white/40">Stock unit</span>
                      <Select value={f.unit || "kg"} onValueChange={(v) => changeUnit(f, v)}>
                        <SelectTrigger data-testid={`supplier-unit-${slug(f.name)}`} className="h-8 w-24 rounded-none bg-[#0F0F0F] border-white/10 text-white text-xs focus:ring-2 focus:ring-white/30"><SelectValue /></SelectTrigger>
                        <SelectContent className="rounded-none bg-[#141414] border-white/10 text-white">
                          {(units || []).map((u) => <SelectItem key={u.id} value={u.name}>{u.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {rows.length === 0 ? (
                    <div className="px-4 py-3 text-xs text-white/40">No colours added to this fabric yet.</div>
                  ) : (
                    <div className="divide-y divide-white/10">
                      {rows.slice(0, itemLimit).map((s) => renderItem(s, unitFallback, false))}
                      {rows.length > itemLimit && (
                        <div className="flex justify-center py-4">
                          <Button onClick={() => setItemLimit((l) => l + 20)} variant="outline" data-testid="supplier-fabric-load-more" className="rounded-none h-9 px-6 border-white/20 bg-transparent text-white hover:bg-white hover:text-black text-xs uppercase tracking-wider transition-colors">
                            Load More ({rows.length - itemLimit} more)
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Order History — confirmed receipts log (admin-only) */}
            <div className="border border-white/10" data-testid="supplier-receipts">
              <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-2.5 border-b border-white/10 bg-[#141414]">
                <span className="font-display font-bold text-white">Order History <span className="text-white/40 text-sm">({filteredReceipts.length}{filteredReceipts.length !== receipts.length ? ` of ${receipts.length}` : ""})</span></span>
                <Button variant="outline" onClick={downloadReceiptsCSV} data-testid="supplier-receipts-download" className="rounded-none h-8 border-white/20 bg-transparent text-white hover:bg-white hover:text-black text-xs uppercase tracking-wider transition-colors">
                  <Download className="h-3.5 w-3.5 mr-2" /> Download Log
                </Button>
              </div>
              <div className="px-4 py-2.5 border-b border-white/10">
                <div className="relative w-full sm:w-80">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/30" />
                  <Input value={receiptSearch} onChange={(e) => setReceiptSearch(e.target.value)} placeholder="Search date, item, colour, qty or remark…" data-testid="supplier-receipts-search" className="rounded-none h-9 bg-[#141414] border-white/10 pl-10 pr-9 text-white text-sm" />
                  {receiptSearch && <button onClick={() => setReceiptSearch("")} data-testid="supplier-receipts-search-clear" className="absolute right-2 top-1/2 -translate-y-1/2 h-6 w-6 flex items-center justify-center text-white/40 hover:text-white"><X className="h-4 w-4" /></button>}
                </div>
              </div>
              {receipts.length === 0 ? (
                <div className="px-4 py-4 text-xs text-white/40">No confirmed receipts yet for this supplier.</div>
              ) : (
                <div className="divide-y divide-white/10">
                  <div className="grid grid-cols-12 gap-3 px-4 py-2 text-[10px] font-bold uppercase tracking-[0.2em] text-white/40 bg-[#0F0F0F]">
                    <div className="col-span-3"><ColumnFilter label="Confirmed on" values={colValues("date", receiptDate)} selected={colFilters.date} onToggle={(v) => toggleColFilter("date", v)} onClear={() => clearColFilter("date")} /></div>
                    <div className="col-span-2"><ColumnFilter label="Item" values={colValues("fabric", (r) => r.fabric || "")} selected={colFilters.fabric} onToggle={(v) => toggleColFilter("fabric", v)} onClear={() => clearColFilter("fabric")} /></div>
                    <div className="col-span-2"><ColumnFilter label="Colour" values={colValues("color", (r) => r.color || "")} selected={colFilters.color} onToggle={(v) => toggleColFilter("color", v)} onClear={() => clearColFilter("color")} /></div>
                    <div className="col-span-2"><ColumnFilter label="Qty" values={colValues("qty", receiptQty)} selected={colFilters.qty} onToggle={(v) => toggleColFilter("qty", v)} onClear={() => clearColFilter("qty")} /></div>
                    <div className="col-span-3"><ColumnFilter label="Remarks" align="right" values={colValues("remark", (r) => r.remark || "")} selected={colFilters.remark} onToggle={(v) => toggleColFilter("remark", v)} onClear={() => clearColFilter("remark")} /></div>
                  </div>
                  {filteredReceipts.length === 0 && (
                    <div className="px-4 py-4 text-xs text-white/40" data-testid="supplier-receipts-none">No receipts match the current search / filters.</div>
                  )}
                  {filteredReceipts.slice(0, receiptLimit).map((r) => (
                    <div key={r.id} className="grid grid-cols-12 gap-3 px-4 py-2.5 text-sm items-start" data-testid={`receipt-row-${r.id}`}>
                      <div className="col-span-3 text-white/60 text-xs tabular">{formatDateTime(r.confirmed_at)}</div>
                      <div className="col-span-2 text-white break-words">{r.fabric}{codeOf(r.fabric) ? <span className="ml-1 text-[10px] text-white/40">({codeOf(r.fabric)})</span> : null}</div>
                      <div className="col-span-2 text-white/80 break-words">{r.color}</div>
                      <div className="col-span-2 text-right text-emerald-400 tabular">{fmt(r.quantity)} {r.unit}</div>
                      <div className="col-span-3 flex items-start justify-between gap-2">
                        <span className="text-white/70 text-xs break-words whitespace-pre-wrap flex-1" data-testid={`receipt-remark-${r.id}`}>{r.remark || "—"}</span>
                        <button onClick={() => doUndoReceipt(r)} data-testid={`receipt-undo-${r.id}`} title="Undo this receipt — roll stock & pending back" className="shrink-0 inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-amber-400/80 hover:text-amber-300 border border-amber-500/30 hover:border-amber-400 px-1.5 py-0.5 transition-colors">
                          <Undo2 className="h-3 w-3" /> Undo
                        </button>
                      </div>
                    </div>
                  ))}
                  {filteredReceipts.length > receiptLimit && (
                    <div className="flex justify-center py-4">
                      <Button onClick={() => setReceiptLimit((l) => l + 20)} variant="outline" data-testid="supplier-receipts-load-more" className="rounded-none h-9 px-6 border-white/20 bg-transparent text-white hover:bg-white hover:text-black text-xs uppercase tracking-wider transition-colors">
                        Load More ({filteredReceipts.length - receiptLimit} more)
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
